import {
    memberSchema, memberEmailSchema, zinreloSchema, memberPhoneSchema,
} from 'store/schemas/database.schemas';
import { putDatabase } from 'store/actions/database.actions';
import MembersRepository from 'services/repositories/MembersRepository';
import { get } from 'lodash';
import { normalize } from 'normalizr';
import { actionCreators } from 'store/actions/metaDatabase.members.actions';
import { buildRepositoryAction, containsIncludes, saveEntity } from './helpers';
import { loadMemberAssistants, memberAssistantsIncludes } from './memberAssistants.dispatchers';
import { buildMetaMemberSelector } from '../selectors/members.selector';

export const travelersIncludes = [
    'memberEmails',
    'memberPhones',
    'travelAgency',
    'additionalFields',
    'memberCreditCardDescriptions',
    'travelerPreferences',
];

export const assistantsIncludes = memberAssistantsIncludes.map((assistantsInclude) => `memberAssistant.${assistantsInclude}`);

export const MEMBER_DISPATCHERS_CONSTANTS = {
    REGULAR_TRAVELERS: '_REGULAR_TRAVELERS_',
};

export function loadMe()
{
    return async () =>
    {
        const repository = new MembersRepository();
        const response = await repository.me();

        if (get(response, 'response.id'))
        {
            return response.response;
        }

        return null;
    };
}

export function saveMember(data, includes = null)
{
    return saveEntity(data.id, actionCreators, buildRepositoryAction(new MembersRepository(), 'save', [data, includes]), memberSchema);
}
export function saveMemberLogo(data)
{
    return async (dispatch) =>
    {
        const repository = new MembersRepository();
        const result = await repository.saveMemberLogo(data.id, data);

        if (result.response_code !== 200)
        {
            if (result.messages && result.messages.length > 0)
            {
                throw new Error(result.messages[0]);
            }
            throw new Error('Error sending information, try again!');
        }

        const normalized = normalize(result.response, memberSchema);
        dispatch(putDatabase(normalized.entities));

        return { data: result.response, response_code: result.response_code };
    };
}
export function updateMemberEmail(id, memberEmailId, data)
{
    return async (dispatch) =>
    {
        const repository = new MembersRepository();
        const result = await repository.updateMemberEmail(id, memberEmailId, data);

        if (result.response_code !== 200)
        {
            if (result.messages && result.messages.length > 0)
            {
                throw new Error(result.messages[0]);
            }
            throw new Error('Error sending information, try again!');
        }

        const normalized = normalize(result.response, memberEmailSchema);
        dispatch(putDatabase(normalized.entities));

        return result.response;
    };
}

export function saveMemberEmail(id, data)
{
    return async (dispatch) =>
    {
        const repository = new MembersRepository();
        const result = await repository.saveMemberEmail(id, data);

        if (result.response_code !== 200)
        {
            if (result.messages && result.messages.length > 0)
            {
                throw new Error(result.messages[0]);
            }
            throw new Error('Error sending information, try again!');
        }

        const normalized = normalize(result.response, memberEmailSchema);
        dispatch(putDatabase(normalized.entities));

        return result.response;
    };
}

export function saveProfileMember(data)
{
    return async () =>
    {
        const repository = new MembersRepository();
        return repository.edit(data);
    };
}

export function loadRegularTravels(cursor)
{
    return async (dispatch, getState) =>
    {
        const metaReportsSelector = buildMetaMemberSelector();
        const metaEntity = metaReportsSelector(getState(), MEMBER_DISPATCHERS_CONSTANTS.REGULAR_TRAVELERS);

        if (metaEntity.loading) return;

        dispatch(actionCreators._loading(MEMBER_DISPATCHERS_CONSTANTS.REGULAR_TRAVELERS, true));
        const repository = new MembersRepository();
        const r = await repository.getRegularTravelers(cursor, travelersIncludes);

        if (!r.isSuccess())
        {
            dispatch(actionCreators._error(MEMBER_DISPATCHERS_CONSTANTS.REGULAR_TRAVELERS, r.mergeMessages()));
        }
        else
        {
            const schemaResult = normalize(r.response.data, [memberSchema]);
            dispatch(putDatabase(schemaResult.entities));

            Object.values(r.response.data).forEach((reservation) =>
            {
                dispatch(actionCreators._setLoadedIncludes(reservation.id, travelersIncludes));
            });
        }

        dispatch(actionCreators._loading(MEMBER_DISPATCHERS_CONSTANTS.REGULAR_TRAVELERS, false));
        // eslint-disable-next-line consistent-return
        return r.response;
    };
}

export function loadAssistants(memberId)
{
    return async (dispatch) =>
    {
        const result = await dispatch(loadMemberAssistants(memberId));
        if (!result)
        {
            return;
        }
        dispatch(actionCreators._setLoadedIncludes(memberId, assistantsIncludes));
    };
}

export function loadRegularTraveler(id, includes = travelersIncludes)
{
    return async (dispatch, getState) =>
    {
        const metaEntitySelector = buildMetaMemberSelector();
        const metaEntity = metaEntitySelector(getState(), id);

        if (metaEntity.loading && containsIncludes(includes, metaEntity.includes))
        {
            return null;
        }

        dispatch(actionCreators._loading(id, true, includes));

        const repository = new MembersRepository();
        const r = await repository.getRegularTraveler(id, includes);

        if (!r.isSuccess())
        {
            dispatch(actionCreators._error(id, r.mergeMessages()));
            dispatch(actionCreators._loading(id, false, includes));
            return r;
        }

        const schemaResult = normalize(r.response.data, memberSchema);
        dispatch(putDatabase(schemaResult.entities));
        dispatch(actionCreators._setLoadedIncludes(id, includes));
        dispatch(actionCreators._loading(id, false));

        return r;
    };
}

export function loadZinreloData(email)
{
    return async (dispatch) =>
    {
        const repository = new MembersRepository();
        const result = await repository.getByEmailWithZinreloData();

        const zinrelo = { id: email, ...result.response };
        const schemaResult = normalize(zinrelo, zinreloSchema);
        dispatch(putDatabase(schemaResult.entities));

        return result.response;
    };
}

export function updateMemberPhone(id, memberPhoneId, data)
{
    return async (dispatch) =>
    {
        const repository = new MembersRepository();
        const response = await repository.updateMemberPhone(id, memberPhoneId, data);

        if (response.response_code !== 200)
        {
            if (response.messages && response.messages.length > 0)
            {
                throw new Error(response.messages[0]);
            }
            throw new Error('Error sending information, try again!');
        }

        const normalized = normalize(response.response, memberPhoneSchema);
        dispatch(putDatabase(normalized.entities));

        return response.response;
    };
}

export function saveMemberPhone(id, data)
{
    return async (dispatch) =>
    {
        const repository = new MembersRepository();
        const response = await repository.saveMemberPhone(id, data);

        if (response.response_code !== 200)
        {
            if (response.messages && response.messages.length > 0)
            {
                throw new Error(response.messages[0]);
            }
            throw new Error('Error sending information, try again!');
        }

        const normalized = normalize(response.response, memberPhoneSchema);
        dispatch(putDatabase(normalized.entities));

        return response.response;
    };
}
